import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import PlainDate from '@Domain/PlainDate';
import InternalInterviewParticipantView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewParticipantView';
import InternalInterviewConsentView from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/InternalInterviewConsentView';
import InternalInterviewDetailsView from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/InternalInterviewDetailsView';
import findLocalInterviewMatch from '@UI/Pages/Beneficiaries/Hooks/Helpers/findLocalInterviewMatch';
import resolveServerParticipants, {
	type InterviewParticipantLabels,
} from '@UI/Pages/Beneficiaries/Hooks/Helpers/resolveServerParticipants';
import type InterviewView from '@UI/Store/Interviews/Views/InterviewView';

/** Which side of the two-origins split the detail screen was asked to open. */
export type InterviewDetailsSource =
	| { readonly type: 'server'; readonly interviewId: number }
	| { readonly type: 'local'; readonly localInterviewId: string };

/**
 * Finds the two faces of the subject when the route names a server interview: `localInterview` via
 * `findLocalInterviewMatch`, `interviewId` first — a fact written locally by `attachServerIdentity`
 * — falling back to `clientReference`. Unlike the interview list, there is no already-matched set to
 * exclude here: the detail screen resolves a single subject, never a batch.
 */
const findSubjectFromServerTarget = (
	interviewId: number,
	serverInterviews: readonly InterviewView[],
	localInterviews: readonly LocalInterview[],
): { readonly serverInterview: InterviewView | null; readonly localInterview: LocalInterview | null } => {
	const serverInterview = serverInterviews.find((interview) => interview.id === interviewId) ?? null;
	const clientReference = serverInterview?.clientReference ?? null;

	const localInterview = findLocalInterviewMatch(interviewId, clientReference, localInterviews);

	return { serverInterview, localInterview };
};

/**
 * Finds the two faces of the subject when the route names a local interview. The `clientReference`
 * fallback is attempted even when `localInterview` is `null`: this is exactly the window that opens
 * right after a transfer's confirmation, once the reconciliation has already deleted the local
 * interview while the page is still open on its local route. Without this fallback the page would
 * empty itself at the precise moment it should be announcing success.
 */
const findSubjectFromLocalTarget = (
	localInterviewId: string,
	serverInterviews: readonly InterviewView[],
	localInterviews: readonly LocalInterview[],
): { readonly serverInterview: InterviewView | null; readonly localInterview: LocalInterview | null } => {
	const localInterview = localInterviews.find((interview) => interview.id === localInterviewId) ?? null;

	const serverInterviewByInterviewId =
		localInterview !== null && localInterview.interviewId !== null
			? (serverInterviews.find((interview) => interview.id === localInterview.interviewId) ?? null)
			: null;

	if (serverInterviewByInterviewId !== null) {
		return { serverInterview: serverInterviewByInterviewId, localInterview };
	}

	// Tried even when `localInterview` is `null`: right after a transfer's confirmation, the
	// reconciliation has already deleted the local interview while the page may still be open on its
	// local route, and the server row carrying that `clientReference` is what keeps the page from
	// emptying itself at the moment it should be announcing success.
	const serverInterviewByClientReference =
		serverInterviews.find((interview) => interview.clientReference === localInterviewId) ?? null;

	return { serverInterview: serverInterviewByClientReference, localInterview };
};

/**
 * The server row wins whenever it exists. Failing that, this device's own `consentGrantedAt` becomes
 * an unsigned consent, recorded nowhere but here: the fleet is offline-first, and the reader on the
 * ground must never see "Consentement absent" for a consent they just collected because the network
 * has not confirmed it yet. `grantedByName` and `consentId` stay `null` on that path — the name of
 * who consented is written server-side, at composite creation, never guessed from the current user.
 */
const resolveConsent = (
	serverInterview: InterviewView | null,
	localInterview: LocalInterview | null,
): InternalInterviewConsentView | null => {
	if (serverInterview !== null && serverInterview.consent !== null) {
		const consent = serverInterview.consent;

		return new InternalInterviewConsentView({
			isWithdrawn: consent.isWithdrawn,
			grantedAt: consent.grantedAt,
			grantedByName: consent.grantedByName,
			withdrawnAt: consent.withdrawnAt,
			withdrawnByName: consent.withdrawnByName,
			consentId: consent.id,
		});
	}

	if (localInterview !== null && localInterview.consentGrantedAt !== null) {
		return new InternalInterviewConsentView({
			isWithdrawn: false,
			grantedAt: new Date(localInterview.consentGrantedAt),
			grantedByName: null,
			withdrawnAt: null,
			withdrawnByName: null,
			consentId: null,
		});
	}

	return null;
};

const resolveOutboxItem = (
	localInterview: LocalInterview | null,
	outboxItems: readonly OutboxItem[],
): OutboxItem | null => {
	if (localInterview === null || localInterview.outboxItemId === null) {
		return null;
	}

	return outboxItems.find((outboxItem) => outboxItem.id === localInterview.outboxItemId) ?? null;
};

const resolveConductedOn = (
	serverInterview: InterviewView | null,
	localInterview: LocalInterview | null,
): PlainDate | null => {
	if (serverInterview !== null) {
		return serverInterview.conductedOn;
	}

	if (localInterview !== null) {
		return PlainDate.fromApi(localInterview.conductedOn);
	}

	return null;
};

/** The server row wins whenever it exists; a local record predating the type counts as a conversation. */
const resolveKind = (
	serverInterview: InterviewView | null,
	localInterview: LocalInterview | null,
): InterviewKind => {
	if (serverInterview !== null) {
		return serverInterview.knownKind;
	}

	return localInterview?.kind ?? InterviewKind.CONVERSATION;
};

/** The server row wins whenever it exists, same precedence as `resolveConductedOn`. */
const resolveName = (
	serverInterview: InterviewView | null,
	localInterview: LocalInterview | null,
): string | null => {
	if (serverInterview !== null) {
		return serverInterview.name;
	}

	return localInterview?.name ?? null;
};

/**
 * Resolves the subject of the detail screen from the two sources an interview can live in, plus the
 * transfer queue. Pure: no port, no `Infra/` import, no side effect, no internal clock — colocated
 * under this hook rather than `Application/` for the same reason as `mergeInterviewEntries`, which it
 * mirrors in shape (types, private helpers, docblocks): it reads `InterviewView`, and `Application/`
 * never imports `@UI`.
 *
 * Returns `null` when neither side of the subject is found: the caller alone decides whether that
 * means "not loaded yet" or "genuinely unknown".
 */
const resolveInterviewDetail = (
	target: InterviewDetailsSource,
	serverInterviews: readonly InterviewView[],
	localInterviews: readonly LocalInterview[],
	outboxItems: readonly OutboxItem[],
	labels: InterviewParticipantLabels,
): InternalInterviewDetailsView | null => {
	const { serverInterview, localInterview } =
		target.type === 'server'
			? findSubjectFromServerTarget(target.interviewId, serverInterviews, localInterviews)
			: findSubjectFromLocalTarget(target.localInterviewId, serverInterviews, localInterviews);

	if (serverInterview === null && localInterview === null) {
		return null;
	}

	// The local port hands back plain objects read from device storage, never an instance of this
	// layer's own View, so each one is wrapped here rather than assigned verbatim.
	const participants =
		serverInterview !== null
			? resolveServerParticipants(serverInterview, labels)
			: (localInterview?.participants.map(
					(participant) => new InternalInterviewParticipantView(participant),
				) ?? []);

	const conductedOn = resolveConductedOn(serverInterview, localInterview);

	return new InternalInterviewDetailsView({
		origin: serverInterview !== null ? 'server' : 'local',
		interviewId: serverInterview?.id ?? localInterview?.interviewId ?? null,
		localInterviewId: localInterview?.id ?? serverInterview?.clientReference ?? null,
		conductedOn,
		kind: resolveKind(serverInterview, localInterview),
		name: resolveName(serverInterview, localInterview),
		participants,
		consent: resolveConsent(serverInterview, localInterview),
		outboxItem: resolveOutboxItem(localInterview, outboxItems),
		recording: serverInterview?.recording ?? null,
		preparationId: serverInterview?.preparationId ?? null,
		transcriptionIssue: serverInterview?.transcriptionIssue ?? null,
		processingState: serverInterview?.processingState ?? null,
	});
};

export default resolveInterviewDetail;
